import { Link } from 'react-router-dom';
import BlogPostLayout from '../components/BlogPostLayout';
import type { ContentsItem } from '../components/TableOfContents';
import { Helmet } from "react-helmet-async";

const INTRODUCING_REHAB_CONTENTS: ContentsItem[] = [
  { level: 'h2', text: 'The Physical Rehab Centre at a glance', id: 'at-a-glance' },
  { level: 'h2', text: 'What is the Physical Rehab Centre?', id: 'what-is' },
  { level: 'h2', text: 'Who will the Physical Rehab Centre cater for?', id: 'who-for' },
  { level: 'h2', text: 'How long can you stay?', id: 'how-long' },
  { level: 'h2', text: 'What conditions does it cover?', id: 'conditions' },
  { level: 'h3', text: 'Orthopaedic recovery', id: 'orthopaedic' },
  { level: 'h3', text: 'Neurological rehabilitation', id: 'neurological' },
  { level: 'h3', text: 'Cardiac and respiratory reconditioning', id: 'cardiac-respiratory' },
  { level: 'h3', text: 'Frailty, falls risk and functional decline', id: 'frailty' },
  { level: 'h3', text: 'Sports injury rehabilitation', id: 'sports' },
  { level: 'h3', text: "Women's health rehabilitation", id: 'womens-health' },
  { level: 'h2', text: 'How do fees work?', id: 'fees' },
  { level: 'h2', text: 'What equipment will be used to assist recovery?', id: 'equipment' },
  { level: 'h3', text: 'AlterG Anti-Gravity Treadmill', id: 'alterg' },
  { level: 'h3', text: 'HUR SmartBalance System', id: 'hur-smartbalance' },
  { level: 'h3', text: 'HUR Multifunction Rehabilitation Machines', id: 'hur-multifunction' },
  { level: 'h2', text: 'What specialist staff will assist in my recovery?', id: 'specialist-staff' },
  { level: 'h2', text: 'How does referral work?', id: 'referral' },
  { level: 'h2', text: 'What happens when you arrive?', id: 'when-you-arrive' },
  { level: 'h2', text: 'Next steps', id: 'next-steps' },
];

function IntroducingProactiveRehabCentre() {
  return (
    <>
    <Helmet>
        <title> Introducing the new Physical Rehab Centre </title>
        <meta
          name="description"
          content="Our new Physical Rehab Centre is designed to support recovery after hospital, surgery, illness or functional decline."
        />
        <link rel="canonical" href="https://www.whitstable.house/help-and-advice/post/introducing-whitstable-house-physical-rehab-centre"/>
      </Helmet>

    <BlogPostLayout title="Introducing Physical Rehab Centre" contents={INTRODUCING_REHAB_CONTENTS}>
      <div className="blog-post-content">
        <p>Recovery can feel like a big leap — especially when you've had surgery, been in hospital, or noticed your strength and confidence slipping. Many people don't need to stay in an acute hospital bed, but they do need more support than weekly physio sessions or trying to manage alone at home.</p>
        <p>That's exactly the gap our new service is designed to fill.</p>
        <p>The Physical Rehab Centre is situated on the top floor of <Link to="/care-suites" className="nurse-care-link">Whitstable House Care Suites</Link>, providing structured, intensive rehabilitation programmes in a safe, supportive environment — with the reassurance of 24-hour, nurse-led clinical care.</p>

        <h2 id="at-a-glance">The Physical Rehab Centre at a glance</h2>
        <ul>
          <li>CQC-registered accommodation-based rehabilitation service</li>
          <li>Private accommodation with clinical oversight</li>
          <li>Daily physiotherapy with progression-based goal planning</li>
          <li>Advanced specialist rehab equipment (technology rarely available outside hospitals)</li>
          <li>24/7 in-house registered nursing support</li>
          <li>Designed to support recovery after hospital, surgery, illness or functional decline</li>
        </ul>

        <h2 id="what-is">What is the Physical Rehab Centre?</h2>
        <p>The Physical Rehab Centre is an inpatient rehabilitation service for adults who need a structured, intensive recovery programme — not just sessional physiotherapy or home-based exercises.</p>
        <p>In simple terms: you stay with us in private accommodation while you work on recovery with a clear plan, daily support, and close clinical monitoring.</p>
        <p>This model can be particularly helpful if you:</p>
        <ul>
          <li>aren't ready to return home safely,</li>
          <li>need more frequent rehabilitation input to reach your goals, or</li>
          <li>would benefit from a supported setting to rebuild strength, mobility and confidence.</li>
        </ul>
        <p>Our aim is to bridge the gap between acute hospital care and independent living, so you can move forward at the right pace, with the right support.</p>

        <h2 id="who-for">Who will the Physical Rehab Centre cater for?</h2>
        <p>The service is designed for adults who need more than outpatient physiotherapy, including people who are:</p>
        <ul>
          <li>Being discharged early from an acute hospital setting, with a plan to continue rehabilitation</li>
          <li>Recovering from post-operative orthopaedic surgery (for example, hip or knee procedures)</li>
          <li>Living with neurological rehabilitation needs</li>
          <li>Rebuilding stamina after cardiac or respiratory illness</li>
          <li>Experiencing frailty, falls risk, or functional decline</li>
          <li>Recovering from sports injuries requiring structured reconditioning</li>
          <li>Seeking support through women's health rehabilitation, where a targeted, progression-based programme is needed</li>
        </ul>
        <p>Recovery is personal — and it's not always linear. You might have great days and slower days. Our role is to keep you moving in the right direction with a plan that's realistic, safe, and focused on everyday life.</p>

        <h2 id="how-long">How long can you stay?</h2>
        <p>There isn't a one-size-fits-all answer, because the right length of stay depends on:</p>
        <ul>
          <li>your starting point (strength, balance, mobility, confidence),</li>
          <li>your goals (for example, stairs, transfers, walking distance, return to work/sport),</li>
          <li>medical factors and how you're healing,</li>
          <li>how much support you'll have at home.</li>
        </ul>
        <p>Some people benefit from a short, intensive inpatient block, while others need a longer period of supported rehabilitation. Where appropriate, you can also continue your programme through outpatient rehabilitation after discharge — or return for a further rehabilitation block if needed.</p>
        <p>What matters most is that your programme is reviewed and progressed, rather than rushed.</p>

        <h2 id="conditions">What conditions does it cover?</h2>
        <p>The Physical Rehab Centre is designed to support a wide range of rehabilitation needs, including:</p>

        <h3 id="orthopaedic">Orthopaedic recovery</h3>
        <p>A structured environment can be ideal for:</p>
        <ul>
          <li>early mobilisation after surgery,</li>
          <li>strength rebuilding,</li>
          <li>gait retraining,</li>
          <li>safe confidence-building with walking and stairs.</li>
        </ul>
        <p>We also offer an Orthopaedic Early Recovery Pathway, where some patients (including many hip and knee replacements) may be able to transfer directly from hospital and begin mobilisation immediately within a supported setting — working collaboratively with discharge teams and consultants.</p>

        <h3 id="neurological">Neurological rehabilitation</h3>
        <p>For people recovering from neurological conditions where rehab needs can be complex and longer-term, we provide:</p>
        <ul>
          <li>detailed movement and balance assessment,</li>
          <li>progression-based exercise and functional training,</li>
          <li>structured monitoring and planning.</li>
        </ul>

        <h3 id="cardiac-respiratory">Cardiac and respiratory reconditioning</h3>
        <p>If you've been unwell and your stamina has dropped, rehabilitation can focus on:</p>
        <ul>
          <li>gradual reconditioning,</li>
          <li>safe pacing and progression,</li>
          <li>confidence with everyday activity again.</li>
        </ul>

        <h3 id="frailty">Frailty, falls risk and functional decline</h3>
        <p>Sometimes it isn't one event — it's a gradual loss of strength or steadiness. Our programmes can help to:</p>
        <ul>
          <li>rebuild strength safely,</li>
          <li>improve balance,</li>
          <li>reduce falls risk,</li>
          <li>increase independence with daily tasks.</li>
        </ul>

        <h3 id="sports">Sports injury rehabilitation</h3>
        <p>For adults who need more structured support than occasional sessions, rehabilitation can focus on:</p>
        <ul>
          <li>controlled strengthening,</li>
          <li>safe return to movement,</li>
          <li>measurable progress and confidence.</li>
        </ul>

        <h3 id="womens-health">Women's health rehabilitation</h3>
        <p>Where appropriate, programmes can support women who need structured rehabilitation and reconditioning — with goals tailored to function, comfort, strength and confidence.</p>

        <h2 id="fees">How do fees work?</h2>
        <p>Fees are usually shaped around your individual programme and needs, such as:</p>
        <ul>
          <li>whether you are staying as an inpatient (with accommodation and nursing support),</li>
          <li>the level of clinical support required,</li>
          <li>the rehabilitation intensity and planned progression,</li>
          <li>whether you continue as an outpatient after discharge.</li>
        </ul>
        <p>Funding routes can include:</p>
        <ul>
          <li><strong>Private self-funding</strong></li>
          <li><strong>Private medical insurance</strong> (policy requirements vary, so it's always worth checking directly with your insurer)</li>
          <li><strong>NHS physiotherapy pathways</strong>, which typically require a GP or consultant referral</li>
        </ul>
        <p>We'll always explain the options clearly and talk you through the next steps so you understand what to expect.</p>

        <h2 id="equipment">What equipment will be used to assist recovery?</h2>
        <p>One of the key features of the Physical Rehab Centre is access to advanced rehabilitation technology that is rarely available outside hospital settings.</p>

        <h3 id="alterg">AlterG Anti-Gravity Treadmill</h3>
        <p>This can support:</p>
        <ul>
          <li>precise body-weight support,</li>
          <li>early weight-bearing after surgery,</li>
          <li>gait retraining and symmetry work,</li>
          <li>a fall-safe environment for mobilisation.</li>
        </ul>

        <h3 id="hur-smartbalance">HUR SmartBalance System</h3>
        <p>This helps with:</p>
        <ul>
          <li>objective balance assessment,</li>
          <li>identifying falls risk,</li>
          <li>cognitive-motor training (where balance and thinking skills interact),</li>
          <li>tracking progress over time.</li>
        </ul>

        <h3 id="hur-multifunction">HUR Multifunction Rehabilitation Machines</h3>
        <p>Designed specifically for rehabilitation populations, these machines support:</p>
        <ul>
          <li>controlled, low-impact strength rebuilding,</li>
          <li>multi-muscle group training,</li>
          <li>joint rehabilitation with safe range limits,</li>
          <li>progression that can be measured and adjusted.</li>
        </ul>
        <p>In practice, this means your rehabilitation can be safer, more targeted, and more measurable — with a clearer picture of where you're improving and what to focus on next.</p>

        <h2 id="specialist-staff">What specialist staff will assist in my recovery?</h2>
        <p>Rehabilitation at Whitstable House is supported by both clinical governance and a strong day-to-day care team.</p>
        <p>The service is led by a Chartered Physiotherapist (MCSP) who is registered with the Health and Care Professions Council (HCPC) and has 14 years' clinical experience across NHS and private practice.</p>
        <p>Chartered physiotherapists are required to:</p>
        <ul>
          <li>maintain ongoing CPD,</li>
          <li>practise in line with evidence-based guidelines,</li>
          <li>follow strict professional and ethical standards.</li>
        </ul>
        <p>Alongside physiotherapy leadership, the unit is supported by 24/7 in-house registered nursing within Whitstable House Care Suites. This is particularly reassuring for anyone who needs:</p>
        <ul>
          <li>medication oversight,</li>
          <li>ongoing observation,</li>
          <li>a clinically supported setting whilst rebuilding independence.</li>
        </ul>
        <p>Rehabilitation is also about coordination — so where appropriate, we can liaise with your wider healthcare team and refer you onwards for consultant review or diagnostic imaging (such as X-ray or MRI) if needed.</p>

        <h2 id="referral">How does referral work?</h2>
        <p>Referral is designed to be straightforward, with different routes depending on how you're accessing the service.</p>

        <p><strong>Private attendance</strong></p>
        <ul>
          <li>No GP referral is required</li>
          <li>You can self-refer directly</li>
          <li>If you're using private medical insurance, many insurers do not require a GP referral — but it's best to confirm your individual policy requirements.</li>
        </ul>

        <p><strong>NHS physiotherapy referral</strong></p>
        <ul>
          <li>This typically requires a GP or consultant referral.</li>
        </ul>

        <p>We welcome referrals from:</p>
        <ul>
          <li>Consultants</li>
          <li>GPs</li>
          <li>Hospital discharge teams</li>
          <li>Case managers</li>
          <li>Private insurers</li>
        </ul>

        <h2 id="when-you-arrive">What happens when you arrive?</h2>
        <p>On admission, you can expect a clear, structured start:</p>
        <ul>
          <li>Comprehensive subjective and objective assessment</li>
          <li>Evaluation of movement, strength and balance</li>
          <li>A clear explanation of findings (in plain English)</li>
          <li>Collaborative goal-setting (what matters to you)</li>
          <li>An individually prescribed programme that is realistic, lifestyle-focused and progression-based</li>
        </ul>
        <p>From there, your rehabilitation is progressed step-by-step — with the aim of helping you leave feeling stronger, safer, and more confident in everyday life.</p>

        <h2 id="next-steps">Next steps</h2>
        <p>If you're exploring rehabilitation for yourself or a family member — whether after a hospital stay, surgery, illness, or a gradual decline in mobility — we're here to help you understand the options and whether an inpatient rehabilitation programme is the right fit.</p>
        <p>Get in touch with the Whitstable House team to discuss your goals, your current level of mobility and support needs, and the most suitable next step (inpatient rehab, outpatient continuation, or onward referral). You can also read more on our <Link to="/rehab" className="nurse-care-link">Physical Rehab Centre page</Link>.</p>

        <section className="content-section get-in-touch-nursing" style={{ marginTop: '2rem' }}>
          <div className="section__container">
            <div className="section__text get-in-touch-content">
              <h2 className="get-in-touch-heading">Get in touch</h2>
              <p>For rehabilitation enquiries and referrals, speak to our Whitstable House team today.</p>
              <p>Call us: <a href="tel:01227533522" className="nurse-care-link">01227 533522</a> (Option 1)</p>
              <p>Email us: <a href="mailto:rehab@whitstable.house" className="nurse-care-link">rehab@whitstable.house</a></p>
              <p>Or complete our <Link to="/contact-us" className="nurse-care-link">enquiry form</Link> and we'll be in touch within 24 hours to discuss rehabilitation options.</p>
            </div>
          </div>
        </section>
      </div>
    </BlogPostLayout>
    </>
  );
}

export default IntroducingProactiveRehabCentre;
